import * as PIXI from "pixi.js";
import type { TextProps, TextStyle } from "./types";
import { hexToNumber } from "../../utils/color";
import { appState } from "../../utils/store";
import { ellipsisText } from "../../utils/text";
import { applyYogaStyle } from "../layoutEngine/applyYogaStyles";
import { calculateTextSize } from "./coreComponents.util";
import { coreComponentCommon } from "./coreComponentsCommon";
import type { NodeStyle } from "../layoutEngine/layout.types";
import { defaultNodeLayoutStyle } from "../layoutEngine/layoutConstants";

export type Text = ReturnType<typeof createText>;

/**
 * TODO: Change _ prefixed private members to # prefixed private members
 * once we start targeting ES2015
 */
export const createText = (initialProps: TextProps) => {
  const textStyle = new PIXI.TextStyle({
    fontFamily: defaultTextStyle.fontFamily,
    fontSize: defaultTextStyle.fontSize,
    fontWeight: defaultTextStyle.fontWeight,
    fill: hexToNumber(defaultTextStyle.color)
  });
  let wasVisible: boolean = true;
  let props: TextProps & { style: NodeStyle } = structuredClone(defaultProps);
  const pixiComponent = new PIXI.Text(props.text, textStyle);
  pixiComponent.position.set(-Infinity, -Infinity);

  const {
    yogaNode,
    getLayoutValues,
    setLayoutValues,
    getZIndex,
    isCulled,
    setCulled
  } = coreComponentCommon();
  updateProps(initialProps);

  return {
    get props() {
      return props;
    },
    get pixiComponent() {
      return pixiComponent;
    },
    get yogaNode() {
      return yogaNode;
    },
    isCulled,
    setCulled,
    getLayoutValues,
    setLayoutValues,
    getZIndex,
    updateProps,
    destroy,
    render
  };

  /**
   * Performs some basic comparison between new and old props and
   * triggers appropriate actions based on certain props' changes.
   * For Box component, these include:
   * 1. Updating text style
   * 2. Updating text
   * 3. Toggling visibility
   * 4. Recalculating text's width based on new style and/or new text
   *
   * @param newProps
   */
  function updateProps(newProps: TextProps) {
    let shouldUpdateTextStyle = false;
    let shouldUpdateText = false;
    if (newProps.text !== props.text) {
      shouldUpdateText = true;
      shouldUpdateTextStyle = true;
    }

    if (newProps.textStyle !== props.textStyle && newProps.textStyle) {
      shouldUpdateTextStyle = true;
    }

    props = {
      ...defaultProps,
      ...newProps,
      style: { ...defaultProps.style, ...newProps.style },
      textStyle: { ...defaultTextStyle, ...newProps.textStyle }
    };

    if (shouldUpdateTextStyle && props.textStyle) {
      updateTextStyle(props.textStyle);
    }

    applyYogaStyle({ style: props.style, node: yogaNode });
    if (shouldUpdateText || shouldUpdateTextStyle) {
      pixiComponent.text = ellipsisText(props.text, {
        maxWidth: props.textStyle?.maxWidth,
        nodeWidth: Number(props.style.width)
      });
    }
  }

  function updateTextStyle(style: Partial<TextStyle>) {
    textStyle.fontFamily = style.fontFamily || textStyle.fontFamily;
    textStyle.fontSize = style.fontSize || textStyle.fontSize;
    textStyle.fontWeight = style.fontWeight || textStyle.fontWeight;
    textStyle.fill =
      style.color !== undefined ? hexToNumber(style.color) : textStyle.fill;
    textStyle.fontStyle = style.italic ? "italic" : "normal";

    props.style = {
      ...props.style,
      ...calculateTextSize(props.text, textStyle)
    };
  }

  function render() {
    const { left, top, width } = getLayoutValues();
    const zIndex = getZIndex();
    const isVisible = props.style.visible;

    const shouldBeVisible: boolean = !!isVisible && !isCulled() && width > 0;

    if (shouldBeVisible !== wasVisible) {
      pixiComponent.visible = shouldBeVisible;
      wasVisible = shouldBeVisible;
    }
    pixiComponent.resolution = appState.resolution;

    pixiComponent.zIndex = zIndex;
    pixiComponent.position.x = left;
    pixiComponent.position.y = top;
  }

  function destroy() {
    // TODO: Implement destroy
    yogaNode.free();
    pixiComponent.destroy();
  }
};

const defaultTextStyle: TextStyle = {
  fontFamily: "Inter",
  fontSize: 14,
  fontWeight: "normal",
  color: "#000000"
};

const defaultProps: TextProps & { style: NodeStyle } = {
  key: "",
  text: "",
  textStyle: defaultTextStyle,
  style: defaultNodeLayoutStyle
};
